// SVG icon components, mirroring the icons used in the RN screens so the
// web UI stays visually consistent with the native app.

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & {
  size?: number;
  color?: string;
};

function Svg({
  size = 22,
  color,
  children,
  ...rest
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function BackIcon({ color = "#1a2e22", ...rest }: IconProps) {
  return (
    <Svg color={color} strokeWidth={2.5} {...rest}>
      <path d="M15 6l-6 6 6 6" />
    </Svg>
  );
}

export function GearIcon({ color = "#1a2e22", ...rest }: IconProps) {
  return (
    <Svg color={color} {...rest}>
      <circle cx={12} cy={12} r={3} />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </Svg>
  );
}

export function HomeIcon({ color = "#2D7A4F", ...rest }: IconProps) {
  return (
    <Svg color={color} {...rest}>
      <path d="M3 11.5L12 4l9 7.5V20a1 1 0 01-1 1h-5v-7h-6v7H4a1 1 0 01-1-1v-8.5z" />
    </Svg>
  );
}

export function LeafIcon({ color = "#ffffff", size = 20, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path
        d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3c.53.13 1.08.2 1.64.2 6 0 10-4.5 12-10 1-3 .5-5.5 0-7-.5.5-2.86 2-7 2-2.5 0-5 1.5-8 2z"
        fill={color}
      />
    </svg>
  );
}

export function DropletIcon({
  color = "#ffffff",
  size = 18,
  ...rest
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path
        d="M12 2.5c-3.5 4.5-7 8.5-7 12.5a7 7 0 0014 0c0-4-3.5-8-7-12.5z"
        fill={color}
      />
    </svg>
  );
}

export function SunIcon({
  color = "#ffffff",
  size = 18,
  ...rest
}: IconProps) {
  return (
    <Svg size={size} color={color} {...rest}>
      <circle cx={12} cy={12} r={4} fill={color} />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </Svg>
  );
}

export function CameraIcon({
  color = "#ffffff",
  size = 20,
  ...rest
}: IconProps) {
  return (
    <Svg size={size} color={color} {...rest}>
      <path d="M4 8h3l2-2.5h6L17 8h3a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1z" />
      <circle cx={12} cy={13} r={3.5} />
    </Svg>
  );
}

export function GalleryIcon({
  color = "#2D7A4F",
  size = 20,
  ...rest
}: IconProps) {
  return (
    <Svg size={size} color={color} {...rest}>
      <path d="M4 5h16v14H4z" />
      <circle cx={9} cy={10} r={1.6} fill={color} />
      <path d="M4 17l4-4 4 4 3-3 5 5" />
    </Svg>
  );
}

export function SparkleIcon({
  color = "#2D7A4F",
  size = 44,
  ...rest
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path
        d="M12 2.5l1.9 5.6 5.6 1.9-5.6 1.9L12 17.5l-1.9-5.6-5.6-1.9 5.6-1.9L12 2.5z"
        fill={color}
      />
      <path
        d="M18.5 14.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2z"
        fill={color}
      />
    </svg>
  );
}

export function PersonIcon({
  color = "#ffffff",
  size = 24,
  ...rest
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <circle cx={12} cy={8} r={4} fill={color} />
      <path
        d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"
        stroke={color}
        strokeWidth={2.5}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

export function AlertIcon({
  color = "#991b1b",
  size = 16,
  ...rest
}: IconProps) {
  return (
    <Svg size={size} color={color} {...rest}>
      <path d="M12 3l10 18H2L12 3z" />
      <path d="M12 10v4M12 17.5v.1" />
    </Svg>
  );
}

export function CheckIcon({
  color = "#2D7A4F",
  size = 16,
  ...rest
}: IconProps) {
  return (
    <Svg size={size} color={color} strokeWidth={2.5} {...rest}>
      <path d="M5 12l5 5 9-11" />
    </Svg>
  );
}

export function GoogleGIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48">
      <path
        fill="#FFC107"
        d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
      />
      <path
        fill="#FF3D00"
        d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
      />
      <path
        fill="#1976D2"
        d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
      />
    </svg>
  );
}
